/**
 * /wireline-slickline/units/new — add a wireline unit
 * (POST /wireline-slickline/units, fieldiq-api#1336). A full page, not a
 * modal. On save the new unit's own page replaces this one.
 *
 * WL_ADMIN only (admin, office_staff). Any other role is told so here rather
 * than handed a form the API would refuse. A 409 shows the API's sentence and
 * links to the unit it names.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateWlUnit, useWlEquipmentOptions, useWlMeta, useWlUnits } from '../../hooks/useWirelineSlickline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminWl, existingIdOf, wlRefusal } from '../../lib/wireline-slickline';
import { useBackToList } from '../downhole/recordPage';
import { UnitForm } from './UnitForm';
import { WL_EYEBROW, WlEmpty, WlLoading, WlPanel } from './WlBits';

export default function WlNewUnitScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/units');
  const { user } = useAuth();
  const canAdd = canAdminWl(user?.role);
  const meta = useWlMeta();
  const units = useWlUnits();
  const equipment = useWlEquipmentOptions(canAdd);
  const create = useCreateWlUnit();
  const [error, setError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const failing = [meta, units, equipment].find((q) => queryLoadFailure(q));

  return (
    <div data-testid="wl-new-unit-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WL_EYEBROW} title="New unit" onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WlPanel eyebrow="Unit register" title="Unit">
            {!canAdd ? (
              <WlEmpty>Only an admin or office staff can add a unit.</WlEmpty>
            ) : meta.isLoading || units.isLoading || equipment.isLoading ? (
              <WlLoading label="Loading form" />
            ) : failing ? (
              <LoadFailureNotice
                error={queryFailureError(failing)}
                kind={queryLoadFailure(failing) || 'unreachable'}
                subject={failing === equipment ? 'your equipment' : 'the unit form'}
              />
            ) : (equipment.data ?? []).length === 0 ? (
              <WlEmpty>A wireline unit is a unit in Equipment, and there is no equipment yet. Add the truck or skid under Equipment first.</WlEmpty>
            ) : (
              <UnitForm
                unit={null}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                equipment={equipment.data ?? []}
                units={units.data?.data ?? []}
                pending={create.isPending}
                error={error}
                duplicateOf={duplicateOf}
                submitLabel="Create unit"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  setDuplicateOf(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success('Unit created');
                      if (res.data?.id) navigate(`/wireline-slickline/units/${res.data.id}`, { replace: true });
                      else navigate('/wireline-slickline/units', { replace: true });
                    },
                    onError: (err) => {
                      setError(wlRefusal(err, 'The unit could not be created.'));
                      setDuplicateOf(existingIdOf(err, 'existingUnitId'));
                    },
                  });
                }}
              />
            )}
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
